import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { SocialIcon } from '../icons/SocialIcon';
import { useSocialPlatforms } from '../../hooks/useSocialPlatforms';
import { executePlatformConnection, invalidateAccountsCache } from '../../utils/socialConnectionUtils';
import type { SocialPlatform } from '../icons/SocialIcon';
import { InstagramOptionsModal } from './InstagramOptionsModal';

interface ConnectAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (platform: SocialPlatform) => void;
  existingPlatforms?: string[];
}

export const ConnectAccountModal: React.FC<ConnectAccountModalProps> = ({
  isOpen,
  onClose,
  onConnect,
  existingPlatforms = []
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [isInstagramOptionsModalOpen, setIsInstagramOptionsModalOpen] = useState(false);
  const { allPlatforms } = useSocialPlatforms();
  const queryClient = useQueryClient();

  if (!isOpen) return null;

  const executePlatformConnectionWrapper = async (platform: SocialPlatform) => {
    try {
      setIsLoading(platform);
      
      queryClient.removeQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      
      await invalidateAccountsCache();
      await executePlatformConnection(platform);
    } catch (error) {
      console.error('Error connecting to platform:', error);
      let errorMessage = 'Failed to connect to platform. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      alert(errorMessage);
      
      // Reason: Only reset loading state on error, not on successful redirect
      setIsLoading(null);
      if (platform === 'instagram' || platform === 'facebook') {
         setIsInstagramOptionsModalOpen(false);
      }
    }
  };

  const handleSocialPlatformSelect = async (platform: SocialPlatform) => {
    if (platform === 'instagram') {
      setIsInstagramOptionsModalOpen(true); 
    } else {
      await executePlatformConnectionWrapper(platform);
    }
  };
  
  const handleCloseInstagramOptionsModal = () => {
    setIsInstagramOptionsModalOpen(false);
  };

  return (
    <>
      <div className={`fixed inset-0 z-50 overflow-y-auto ${isOpen ? 'block' : 'hidden'}`}>
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
          
          <div className="relative bg-white rounded-xl border border-black shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Connect Social Network</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {(() => {
                // Trier les plateformes : actives et beta d'abord, puis coming-soon à la fin
                const sortedPlatforms = allPlatforms
                  .sort((a, b) => {
                    // Priorité : actives/beta (0) < coming-soon (1)
                    const getPriority = (platform: any) => {
                      const isComingSoon = platform.status === 'coming-soon';
                      return isComingSoon ? 1 : 0;
                    };

                    const priorityA = getPriority(a);
                    const priorityB = getPriority(b);

                    if (priorityA !== priorityB) {
                      return priorityA - priorityB;
                    }

                    // Puis par ordre alphabétique dans chaque groupe
                    return a.name.localeCompare(b.name);
                  });

                return sortedPlatforms.map(platform => {
                  const isDisabled = platform.status === 'coming-soon';
                  const isConnecting = isLoading === platform.id;
                  const isBetaPlatform = platform.id === 'pinterest' || platform.id === 'tiktok';
                  return (
                    <div key={platform.id} className="relative">
                      <button
                        onClick={() => !isDisabled && handleSocialPlatformSelect(platform.id)}
                        disabled={isDisabled || isConnecting || (isLoading !== null && isLoading !== platform.id) }
                        className={`w-full flex items-center gap-2 p-3 rounded-lg border ${
                          isDisabled
                            ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                            : 'border-black hover:border-primary-600 hover:bg-primary-50'
                        }`}
                      >
                        <SocialIcon platform={platform.id} size={24} />
                        <div className="flex flex-col items-start">
                          <span className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                            {platform.name}
                          </span>
                          {platform.status === 'coming-soon' && (
                            <span className="text-xs text-gray-400">
                              Coming soon
                            </span>
                          )}
                          {isConnecting && (
                            <span className="text-xs text-primary-600">
                              Connecting...
                            </span>
                          )}
                        </div>
                      </button>
                      {isBetaPlatform && (
                        <span className="absolute -top-1 -right-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-500 text-white">
                          Beta
                        </span>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </div>

      {isInstagramOptionsModalOpen && (
        <InstagramOptionsModal
          isOpen={isInstagramOptionsModalOpen}
          onClose={handleCloseInstagramOptionsModal}
          onConnectViaFacebook={() => executePlatformConnectionWrapper('facebook')}
          onConnectDirectlyInstagram={() => executePlatformConnectionWrapper('instagram')}
          isConnecting={isLoading === 'facebook' || isLoading === 'instagram'}
          connectingPlatform={isLoading}
        />
      )}
    </>
  );
};